import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { FadeIn, useFadeInOnScroll } from "@/lib/hooks";
import { BRAND, IMG, VALUES, EXPERTISE, PROCESS } from "@/lib/data";
import { ABOUT } from "@/constants/testIds";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

export default function About() {
    useFadeInOnScroll();
    const navigate = useNavigate();
    return (
        <main data-testid={ABOUT.root} className="bg-[#0A0A0A] text-[#F1F1EE]">
            {/* HERO */}
            <section data-testid={ABOUT.hero} className="relative h-[80vh] min-h-[540px] overflow-hidden">
                <img src={IMG.heroAbout} alt="Master artisan" className="absolute inset-0 w-full h-full object-cover slow-zoom" />
                <div className="absolute inset-0 bg-black/55" />
                <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6 md:px-16 max-w-[1600px] mx-auto text-white">
                    <FadeIn className="visible">
                        <p className="overline text-white/80">About {BRAND.name}</p>
                    </FadeIn>
                    <FadeIn delay={140} className="visible max-w-4xl mt-6">
                        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl leading-[0.95]">
                            Our <span className="italic">Heritage</span>.
                        </h1>
                    </FadeIn>
                </div>
            </section>

            {/* OUR STORY */}
            <section data-testid={ABOUT.story} className="px-6 md:px-12 py-24 md:py-40 bg-[#0A0A0A]">
                <div className="max-w-[1300px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <FadeIn className="lg:col-span-4">
                        <p className="overline text-[#D4AF37]">Our Story</p>
                        <div className="hairline-gold mt-6" />
                    </FadeIn>
                    <FadeIn delay={100} className="lg:col-span-8">
                        <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.03] text-[#F1F1EE]">
                            Established in the early 1980s to celebrate handcrafted Asian artistry.
                        </h2>
                        <div className="mt-12 space-y-6 text-[#B8B8B8] font-light text-base md:text-lg leading-relaxed max-w-2xl">
                            <p>
                                The Treasure Threads was established in the early 1980s with a vision to preserve and celebrate the rich traditions of handcrafted Asian artistry. Over four decades, the business has grown into a trusted destination for discerning collectors, interior designers and travellers seeking exceptional handcrafted treasures.
                            </p>
                            <p>
                                Our philosophy has always remained unchanged &mdash; offering authentic craftsmanship, timeless elegance and products that carry genuine cultural heritage.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* MISSION */}
            <section data-testid={ABOUT.mission} className="px-6 md:px-12 py-24 md:py-40 premium-dark-bg text-white relative overflow-hidden">
                <div className="relative z-10 max-w-[1200px] mx-auto text-center">
                    <FadeIn>
                        <p className="overline text-[#D4AF37]">Our Mission</p>
                    </FadeIn>
                    <FadeIn delay={120}>
                        <h2 className="font-serif-display text-4xl md:text-6xl mt-8 leading-[1.05] max-w-4xl mx-auto text-[#F1F1EE]">
                            <span className="italic">To preserve traditional craftsmanship</span> while bringing extraordinary handmade creations to collectors around the world.
                        </h2>
                    </FadeIn>
                    <FadeIn delay={220}>
                        <div className="hairline-gold mx-auto mt-12" />
                    </FadeIn>
                </div>
            </section>

            {/* VALUES + EXPERTISE */}
            <section className="px-6 md:px-12 py-24 md:py-40 bg-[#111111]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
                    <FadeIn data-testid={ABOUT.values}>
                        <p className="overline text-[#D4AF37]">Our Values</p>
                        <h3 className="font-serif-display text-4xl md:text-5xl mt-6 text-[#F1F1EE] leading-tight">
                            The principles that quietly guide every purchase.
                        </h3>
                        <ul className="mt-10 divide-y divide-[#1F1F1F] border-t border-b border-[#1F1F1F]">
                            {VALUES.map((v, i) => (
                                <li key={v} className="flex items-baseline justify-between py-5">
                                    <span className="font-serif-display text-2xl md:text-3xl text-[#F1F1EE]">{v}</span>
                                    <span className="text-xs text-[#666666] font-light">0{i + 1}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeIn>
                    <FadeIn delay={150} data-testid={ABOUT.expertise}>
                        <p className="overline text-[#D4AF37]">Our Expertise</p>
                        <h3 className="font-serif-display text-4xl md:text-5xl mt-6 text-[#F1F1EE] leading-tight">
                            Six disciplines, each held to a curator&rsquo;s standard.
                        </h3>
                        <ul className="mt-10 grid grid-cols-1 gap-4">
                            {EXPERTISE.map((e) => (
                                <li key={e} className="border border-[#1F1F1F] bg-[#0A0A0A] px-6 py-5 flex items-center justify-between group hover:border-[#D4AF37] transition-colors">
                                    <span className="font-serif-display text-xl md:text-2xl text-[#F1F1EE]">{e}</span>
                                    <ArrowRight className="w-4 h-4 text-[#F1F1EE] transition-all group-hover:translate-x-1 group-hover:text-[#D4AF37]" strokeWidth={1.4} />
                                </li>
                            ))}
                        </ul>
                    </FadeIn>
                </div>
            </section>

            {/* PROCESS */}
            <section data-testid={ABOUT.process} className="px-6 md:px-12 py-24 md:py-40 bg-[#0A0A0A] border-t border-[#1A1A1A]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <FadeIn className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
                        <p className="overline text-[#D4AF37]">Our Process</p>
                        <h2 className="font-serif-display text-4xl md:text-5xl mt-6 text-[#F1F1EE] leading-tight">
                            From the artisan&rsquo;s hand to your home.
                        </h2>
                        <div className="hairline-gold mt-8" />
                        <p className="mt-8 text-[#A8A8A8] font-light leading-relaxed">
                            Each object in our showroom passes through a careful, unhurried process — one that respects the maker and the buyer equally.
                        </p>
                    </FadeIn>
                    <div className="lg:col-span-8 relative">
                        <span className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-[#1F1F1F]" aria-hidden="true" />
                        <ul className="space-y-14">
                            {PROCESS.map((p, i) => (
                                <FadeIn key={p.step} delay={i * 80} as="li" className="relative pl-16 md:pl-24">
                                    <span className="absolute left-2 md:left-4 top-1 w-9 h-9 rounded-full border border-[#D4AF37] bg-[#0A0A0A] flex items-center justify-center text-[10px] tracking-[0.2em] uppercase font-medium text-[#D4AF37]">
                                        {p.step}
                                    </span>
                                    <h4 className="font-serif-display text-3xl md:text-4xl text-[#F1F1EE]">{p.title}</h4>
                                    <div className="hairline-gold mt-3" />
                                    <p className="mt-4 text-[#A8A8A8] font-light max-w-xl leading-relaxed">{p.detail}</p>
                                </FadeIn>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* MEET OUR COLLECTION */}
            <section className="px-6 md:px-12 py-24 md:py-40 bg-[#111111]">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <FadeIn className="lg:col-span-7 order-2 lg:order-1">
                        <p className="overline text-[#D4AF37]">Meet Our Collection</p>
                        <h2 className="font-serif-display text-4xl md:text-6xl mt-6 text-[#F1F1EE] leading-[1.03]">
                            Chosen with a collector&rsquo;s eye.
                        </h2>
                        <div className="hairline-gold mt-8" />
                        <p className="mt-8 text-[#B8B8B8] font-light text-base md:text-lg leading-relaxed max-w-xl">
                            Every product in our showroom is chosen with a collector&rsquo;s eye — celebrating tradition, craftsmanship and enduring beauty. From a single hand-knotted silk carpet to a rare bronze temple sculpture, each piece is offered because it deserves to be.
                        </p>
                        <Link to="/collections" className="btn-outline-dark group mt-10">
                            View Collections
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                        </Link>
                    </FadeIn>
                    <FadeIn delay={100} className="lg:col-span-5 order-1 lg:order-2">
                        <div className="img-hover-wrap aspect-[3/4]">
                            <img src={IMG.silkAccessories2} alt="Curated collection preview" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* CTA */}
            <section data-testid={ABOUT.cta} className="premium-dark-bg text-white px-6 md:px-12 py-24 md:py-40 relative overflow-hidden">
                <div className="relative z-10 max-w-3xl mx-auto text-center">
                    <p className="overline text-[#D4AF37]">Visit Us</p>
                    <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02]">
                        Come see the collection in person.
                    </h2>
                    <p className="mt-8 flex items-start justify-center gap-3 text-white/80 font-light">
                        <MapPin className="w-5 h-5 mt-1 text-[#D4AF37]" strokeWidth={1.4} />
                        {BRAND.address}
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(BRAND.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary group"
                            data-testid={ABOUT.ctaVisit}
                        >
                            <MapPin className="w-4 h-4" strokeWidth={1.6} /> Visit Our Store
                        </a>
                        <button
                            type="button"
                            onClick={() => navigate("/#consultation")}
                            className="btn-primary group"
                            data-testid={ABOUT.ctaWhatsapp}
                        >
                            <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                            WhatsApp Us
                        </button>
                    </div>
                    <p className="mt-8 text-xs text-white/60 tracking-wider">
                        Or open WhatsApp:&nbsp;
                        <a
                            href={buildWhatsAppUrl("Hello The Treasure Threads, I would like to enquire about your collection.")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-[#D4AF37]"
                        >
                            {BRAND.phone}
                        </a>
                    </p>
                </div>
            </section>
        </main>
    );
}
